// Manufacturing dock signals — stale WAC + plant material requests.

import { useEffect, useMemo, useState } from "react";
import type { AuthContextValue } from "@/contexts/AuthContext";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { getManufacturingMaterialCostingSummary } from "@/lib/manufacturingClient";
import { countStaleWacMaterials } from "@/lib/manufacturing/staleWacCount";
import { listManufacturingPurchaseOrders } from "@/lib/manufacturing/purchaseOrderClient";
import { listOpenQcItems, type OpenQcItem } from "@/lib/manufacturing/builderAssistantClient";
import { listNegativeMaterials } from "@/lib/service/materialUsageClient";
import {
  countUnacknowledgedPlantRequests,
  plantRequestAlertOrders,
  plantRequestPhase,
  plantRequestStatusCopy,
} from "@/lib/manufacturing/plantMaterialRequest";
import {
  canSeePlantRequestStatusFromHasRole,
  canSeePurchaserRequestAlertFromHasRole,
} from "@/lib/manufacturing/plantMaterialRequestAccess";

export type PlantRequestDockItem = {
  id: string;
  poNumber: string;
  copy: string;
};

export type MfgDockSignals = {
  staleWacCount: number | null;
  plantRequestReviewCount: number | null;
  plantRequestStatusItems: PlantRequestDockItem[] | null;
  negativeOnHandCount: number | null;
  openQcItems: OpenQcItem[] | null;
};

export function useMfgDockSignals(
  auth: AuthContextValue | null,
  scopedOrganizationId: string | null,
): MfgDockSignals {
  const [staleWacCount, setStaleWacCount] = useState<number | null>(null);
  const [plantRequestReviewCount, setPlantRequestReviewCount] = useState<number | null>(null);
  const [plantRequestStatusItems, setPlantRequestStatusItems] = useState<
    PlantRequestDockItem[] | null
  >(null);
  const [openQcItems, setOpenQcItems] = useState<OpenQcItem[] | null>(null);
  const [negativeOnHandCount, setNegativeOnHandCount] = useState<number | null>(null);

  const hasRole = useMemo(
    () => (...roles: string[]) => Boolean(auth?.hasRole(...roles)),
    [auth],
  );

  const canFetchWac = useMemo(() => {
    if (!auth || !scopedOrganizationId) return false;
    if (!auth.hasRole("manufacturing")) return false;
    return canAccessPrimaryMode("mfgInventory", auth);
  }, [auth, scopedOrganizationId]);

  const canFetchRequests = useMemo(() => {
    if (!auth || !scopedOrganizationId) return false;
    if (!canAccessPrimaryMode("mfgPurchasing", auth)) return false;
    return (
      canSeePurchaserRequestAlertFromHasRole(hasRole) ||
      canSeePlantRequestStatusFromHasRole(hasRole)
    );
  }, [auth, hasRole, scopedOrganizationId]);

  const canFetchOpenQc = useMemo(() => {
    if (!auth || !scopedOrganizationId) return false;
    return canAccessPrimaryMode("mfgBuilderAssistant", auth);
  }, [auth, scopedOrganizationId]);

  useEffect(() => {
    if (!canFetchWac || !scopedOrganizationId) {
      setStaleWacCount(null);
      return;
    }
    let cancelled = false;
    void getManufacturingMaterialCostingSummary().then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        setStaleWacCount(null);
        return;
      }
      const summary = Array.isArray(result.data.summary) ? result.data.summary : [];
      setStaleWacCount(countStaleWacMaterials(summary));
    });
    return () => {
      cancelled = true;
    };
  }, [canFetchWac, scopedOrganizationId]);

  useEffect(() => {
    if (!canFetchRequests || !scopedOrganizationId) {
      setPlantRequestReviewCount(null);
      setPlantRequestStatusItems(null);
      return;
    }
    let cancelled = false;
    void listManufacturingPurchaseOrders().then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        setPlantRequestReviewCount(null);
        setPlantRequestStatusItems(null);
        return;
      }
      const orders = Array.isArray(result.data.orders) ? result.data.orders : [];
      setPlantRequestReviewCount(countUnacknowledgedPlantRequests(orders));
      setPlantRequestStatusItems(
        plantRequestAlertOrders(orders).map((o) => {
          const phase = plantRequestPhase(o, orders);
          return {
            id: o.id,
            poNumber: o.po_number,
            copy: phase
              ? plantRequestStatusCopy(phase, {
                  expectedDeliveryOn: o.expected_delivery_on,
                  poNumber: o.po_number,
                })
              : "",
          };
        }),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [canFetchRequests, scopedOrganizationId]);

  useEffect(() => {
    if (!canFetchRequests || !scopedOrganizationId) {
      setNegativeOnHandCount(null);
      return;
    }
    let cancelled = false;
    void listNegativeMaterials().then((result) => {
      if (cancelled) return;
      setNegativeOnHandCount(result.ok ? result.data.materials.length : null);
    });
    return () => {
      cancelled = true;
    };
  }, [canFetchRequests, scopedOrganizationId]);

  useEffect(() => {
    if (!canFetchOpenQc) {
      setOpenQcItems(null);
      return;
    }
    let cancelled = false;
    void listOpenQcItems().then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        setOpenQcItems(null);
        return;
      }
      setOpenQcItems(result.data.items);
    });
    return () => {
      cancelled = true;
    };
  }, [canFetchOpenQc]);

  return { staleWacCount, plantRequestReviewCount, plantRequestStatusItems, negativeOnHandCount, openQcItems };
}
